import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { catalogueApi, errorOf, needsMigration, STAGE } from '../../lib/catalogue';
import { fmtDate } from '../../lib/when';
import { ItemIcon } from './RequestCatalogue';
import './Catalogue.css';

/**
 * MY REQUESTS, for a member of staff (migration 124): what they asked for
 * from Request something, newest first - where each one is (waiting for
 * approval, sent back, refused, being done, done) and how far its checklist
 * has got. Each opens its ticket.
 *
 * /my-requests was the contractor's alone (ContractorRequests: every ticket
 * they raised), and staff were sent to My tickets. A contractor - and an
 * administrator viewing as one - still gets exactly that page (App.js);
 * staff and administrators otherwise get this one. Their other tickets stay
 * where they always were, under My tickets.
 */
export default function MyRequests() {
  const [rows, setRows] = useState(null);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    let live = true;
    catalogueApi.mine()
      .then((r) => { if (live) setRows(r); })
      .catch((e) => {
        if (!live) return;
        setProblem(needsMigration(e) ? 'Request something is not set up on this server yet (it needs migration 124).'
          : errorOf(e, 'Could not load your requests.'));
      });
    return () => { live = false; };
  }, []);

  const waiting = (rows || []).filter((r) => r.stage === 'waiting' || r.stage === 'sent_back').length;

  return (
    <div className="container rq-page" data-testid="my-requests">
      <div className="page-header">
        <h1 className="page-title">My requests</h1>
        <p className="rq-lead">
          What you asked for from <Link to="/request">Request something</Link>
          {rows && rows.length > 0 ? ` - ${waiting === 0 ? 'none waiting for approval' : `${waiting} waiting for approval`}.` : '.'}
          {' '}Your other tickets are under <Link to="/my-tickets">My tickets</Link>.
        </p>
      </div>
      {problem && <div className="rq-alert" role="alert">{problem}</div>}
      {!problem && !rows && <p className="rq-lead">Loading…</p>}
      {rows && rows.length === 0 && (
        <div className="rq-empty">You have not asked for anything yet. <Link to="/request">Request something</Link></div>
      )}
      {rows && rows.length > 0 && (
        <div className="rq-list">
          {rows.map((r) => {
            const st = STAGE[r.stage] || STAGE.in_progress;
            const pct = r.steps_total ? Math.round((r.steps_done / r.steps_total) * 100) : 0;
            return (
              <Link key={r.ticket_id} to={`/tickets/${r.ticket_id}`} className="rq-row" data-request={r.ticket_number}>
                <ItemIcon name={r.icon} />
                <span className="rq-row-main">
                  <span className="rq-row-title">{r.item_name}</span>
                  <span className="rq-row-sub">
                    {[r.ticket_number, fmtDate(r.created_at), r.cost_words].filter(Boolean).join(' · ')}
                  </span>
                  <span className="rq-row-sub">
                    {r.stage === 'refused' && r.rejection_reason ? `Refused: ${r.rejection_reason}` : r.approval_said}
                  </span>
                </span>
                <span className="rq-row-side">
                  <span className="rq-stage" data-tone={st.tone}>{st.label}</span>
                  {r.steps_total > 0 && (
                    <>
                      <span className="rq-row-sub">{`${r.steps_done} of ${r.steps_total} steps done`}</span>
                      <span className="rq-progress" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
                    </>
                  )}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
